import { useNavigate, useLocation } from 'react-router-dom';
import { Package, Factory, ArrowRightLeft, Trash2, BarChart3, Settings, LogOut, Home, ShoppingCart, BookOpen, Receipt, Cog, ClipboardList, Users, MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useActiveLocation } from '../context/LocationContext';

export const Layout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeLocation, setActiveLocation, allowedLocations } = useActiveLocation();
  const canSwitch = user?.role === 'admin' && allowedLocations.length > 1;

  const handleSwitch = (id) => {
    const loc = allowedLocations.find((l) => l.location_id === id);
    if (loc) setActiveLocation(loc);
  };

  const navItems = [
    { icon: Home, label: 'Dashboard', path: '/' },
    { icon: ShoppingCart, label: 'POS', path: '/pos' },
    { icon: ClipboardList, label: 'Orders', path: '/orders' },
    { icon: Users, label: 'Customers', path: '/customers' },
    { icon: Receipt, label: 'Sales', path: '/sales' },
    { icon: BookOpen, label: 'Menu', path: '/menu' },
    { icon: Factory, label: 'Production', path: '/production' },
    { icon: Package, label: 'Inventory', path: '/inventory' },
    { icon: ArrowRightLeft, label: 'Transfers', path: '/transfers' },
    { icon: Trash2, label: 'Wastage', path: '/wastage' },
    { icon: BarChart3, label: 'Reports', path: '/reports' },
  ];

  if (user?.role === 'admin') {
    navItems.push({ icon: Settings, label: 'Admin Setup', path: '/admin' });
    navItems.push({ icon: Cog, label: 'Settings', path: '/settings' });
  }

  return (
    <div className="flex h-screen bg-stone-50">
      <aside className="w-64 bg-primary flex flex-col">
        <div className="p-6 border-b border-stone-800">
          <div className="flex items-center gap-3 mb-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_roch-kitchen/artifacts/fdmjtm1k_logo%20%281%29.png" 
              alt="ROCH Logo" 
              className="w-12 h-12 object-contain rounded-full"
            />
            <div>
              <h1 className="font-heading text-xl font-bold text-white">ROCH Studio</h1>
              <p className="text-stone-400 text-xs">{activeLocation?.type === 'central_kitchen' ? 'Central Kitchen' : 'Retail Outlet'}</p>
            </div>
          </div>
          {activeLocation && (
            canSwitch ? (
              <Select value={activeLocation.location_id} onValueChange={handleSwitch}>
                <SelectTrigger data-testid="location-switcher" className="bg-stone-800 border-stone-700 text-white h-9">
                  <div className="flex items-center gap-2 truncate"><MapPin size={14} className="text-secondary" /><SelectValue /></div>
                </SelectTrigger>
                <SelectContent>
                  {allowedLocations.map((l) => (
                    <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div data-testid="active-location-label" className="flex items-center gap-2 bg-stone-800 rounded-sm px-3 h-9 text-sm text-white truncate">
                <MapPin size={14} className="text-secondary shrink-0" /> {activeLocation.name}
              </div>
            )
          )}
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-sm transition-colors ${
                  isActive
                    ? 'bg-secondary text-black font-semibold'
                    : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-stone-800">
          <div className="mb-4 px-2">
            <p className="text-white font-medium">{user?.name}</p>
            <p className="text-stone-400 text-sm truncate">{user?.phone || user?.email}</p>
            <p className="text-secondary text-xs mt-1 uppercase font-mono font-semibold">{user?.role}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-stone-300 hover:bg-stone-800 hover:text-white rounded-sm transition-colors"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};
